import React, { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { cabAction } from "../../../store/Cab/CAbReducer"
import { slugify } from "../../../utils/slugify"
import BookCabForm from "../BookCabForm"
import PaymentCard from "../PaymentCard"
import Hatchback from "../../../assets/cars/hatchback.webp"
import Sedan from "../../../assets/cars/sedan.webp"
import SUV from "../../../assets/cars/suv.webp"
import ProgressStepper from "../../../components/ProgressStepper"
import { trackBeginCheckout } from "../../../utils/analytics"

const getCarImage = (name) => {
  const n = (name || "").toLowerCase().replace(/\s+/g, "")
  if (n === "hatchback") return Hatchback
  if (n === "suv") return SUV
  return Sedan
}

const CAB_TYPE = { 1: "COMPACT", 2: "SEDAN", 3: "SUV" }

const MODEL_NAMES = {
  1: "Swift, WagonR or similar",
  2: "Dzire, Etios or similar",
  3: "Innova, Ertiga or similar",
}

// The fare API returns vehicle_id as a backend DB id (49/50/51 on fleets), not
// the 1/2/3 segment, so the name is the reliable discriminator. Without this the
// badge read "CAB" and the model line "Swift, WagonR" on every booking, even a
// Sedan or SUV. Mirrors the mapping in components/Cards/CabCard.js.
const NAME_TO_ID = { hatchback: 1, sedan: 2, suv: 3 }
const resolveCabTypeId = (cab) =>
  NAME_TO_ID[(cab?.name || "").toLowerCase().replace(/\s+/g, "")] ||
  (cab?.vehicle_id >= 1 && cab?.vehicle_id <= 3 ? cab.vehicle_id : 1)

// Compact "good to know" chips. Cut down from the old five-tile grid + accordion
// so the rider reaches the pay action in far less scroll on mobile. Each is a
// glanceable trust signal, not a paragraph.
const INCLUSIONS = [
  { label: "GST & taxes included" },
  { label: "AC cab" },
  { label: "15-min free wait" },
  { label: "Live trip tracking" },
]

const BookCabMain = ({ restoredTripData = null }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const tripData = restoredTripData || JSON.parse(localStorage.getItem("tripData") || "null")
  const { distance, visitorEntryId } = useSelector((state) => state.cab || {})

  // On a hard refresh of /bookCab the Redux booking context (visitorEntryId /
  // web_trip_unique_id) is lost, but the search result still sits in
  // localStorage. Rehydrate it so createOrder / applyCoupon don't fire with an
  // empty id and fail.
  useEffect(() => {
    if (visitorEntryId) return
    try {
      const cached = JSON.parse(localStorage.getItem("cabSearchResult") || "null")
      if (cached && (cached.visitorEntryId || cached.web_trip_unique_id)) {
        dispatch(cabAction.setCabSearchResult(cached))
      }
    } catch (e) { /* ignore malformed cache */ }
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return ""
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return dateStr
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
  }

  const formatDisplayTime = (timeStr) => {
    if (!timeStr) return ""
    if (timeStr.toLowerCase().includes("am") || timeStr.toLowerCase().includes("pm")) {
      return timeStr
    }
    const [h, m] = timeStr.split(":").map(Number)
    if (isNaN(h)) return timeStr
    const ampm = h >= 12 ? "PM" : "AM"
    const h12 = h % 12 || 12
    return `${h12}:${String(m).padStart(2, "0")} ${ampm}`
  }

  useEffect(() => {
    const stored = localStorage.getItem("tripData")
    if (!stored && !restoredTripData) navigate("/")
    window.scrollTo(0, 0)
  }, [navigate, restoredTripData])

  // Reaching this checkout screen with a chosen cab is begin_checkout. Keyed on
  // the trip id so it fires once per booking, not on every re-render.
  useEffect(() => {
    if (!tripData?.cab) return
    trackBeginCheckout({
      value: Number(parseInt(tripData?.cab?.total)) || undefined,
      currency: "INR",
      route: [tripData?.pickup_city, tripData?.drop_city].filter(Boolean).join(" to ") || undefined,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripData?.web_trip_unique_id])

  const handleExpiryRedirect = () => {
    if (tripData) {
      const from = slugify(tripData.pickup_city)
      const to = tripData.drop_city ? slugify(tripData.drop_city) : null

      if (from && to) navigate(`/routes/${from}-to-${to}-cab/`)
      else if (from) navigate(`/cities/${from}/`)
      else navigate("/")
    } else {
      navigate("/")
    }
  }

  if (!tripData) return (
    <div className="pt-[100px] text-center font-bold text-gray-500">
      <p>Loading booking details...</p>
    </div>
  )

  const cab = tripData.cab
  const cabTypeId = resolveCabTypeId(cab)
  const cabType = CAB_TYPE[cabTypeId] || "CAB"
  const displayName = cab?.name === "Hatchback" ? "Hatch Back" : cab?.name
  // Round trip shows the base running fare; driver allowance + toll are paid at
  // the trip (at actuals) and are not in the headline or collected online. This
  // must match CabCard and PaymentCard so the number never jumps between screens.
  const isRoundTrip = !!(tripData.round_trip || tripData.is_round_trip)
  const driverAllowance = Number(cab?.driver_allowance) || 0
  const fareTotal = Number(parseInt(cab?.total)) || 0
  const priceLabel = isRoundTrip ? Math.max(fareTotal - driverAllowance, 0) : fareTotal

  // Human-readable trip type for the hero chip. Mirrors the convention used in
  // PaymentCard (3 = Outstation, 2 = Rental) so the label never contradicts the
  // payment logic; middot separators keep to the no-dash house rule.
  const tripTypeLabel =
    tripData.trip_type === 3
      ? tripData.round_trip || tripData.is_round_trip
        ? "Outstation · Round trip"
        : "Outstation · One-way"
      : tripData.trip_type === 2
      ? "Rental"
      : "Local"

  return (
    <div className="bg-[#f9fafa] text-[#171d1b] min-h-screen font-body pb-28 lg:pb-16">
      {/* Header */}
      <nav className="w-full bg-white border-b border-gray-100 flex items-center justify-between px-4 md:px-12 h-14 md:h-20 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
        <button onClick={handleExpiryRedirect} className="flex items-center shrink-0">
          <img src="/images/Newlogo.webp" alt="Pulpit Cabs" className="h-[32px] md:h-[46px] w-auto" />
        </button>

        <a href="tel:+918052080501" className="flex items-center gap-2 md:gap-3 hover:opacity-80 transition-opacity duration-200 shrink-0 ml-3">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#21A5A0" strokeWidth="2" className="shrink-0 md:w-[22px] md:h-[22px]"><path d="M15.05 5A5 5 0 0 1 19 8.95M15.05 1A9 9 0 0 1 23 8.94m-1 7.98v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
          <div className="flex flex-col">
            <span className="text-[9px] md:text-[10px] text-[#6b7280] font-bold uppercase tracking-wider leading-tight">Need Help?</span>
            <span className="text-[13px] md:text-[16px] font-black text-[#171d1b] leading-tight tracking-tight whitespace-nowrap">+91 80520-80501</span>
          </div>
        </a>
      </nav>

      {/* Progress Stepper */}
      <ProgressStepper currentStep={3} />

      {/* Slim hero: trip summary at a glance, minimal height so the pay action
          is close on mobile. The content cards lift over its lower edge. */}
      <header className="relative overflow-hidden bg-gradient-to-br from-[#23aaa5] via-[#1f9c98] to-[#16807c] text-white">
        <svg
          aria-hidden="true"
          className="absolute inset-0 h-full w-full pointer-events-none"
          preserveAspectRatio="xMidYMid slice"
          viewBox="0 0 1200 220"
          fill="none"
        >
          <path d="M0 150 Q 300 80 600 132 T 1200 120 V220 H0 Z" fill="#ffffff" fillOpacity="0.09" />
          <circle cx="1060" cy="40" r="110" fill="#ffffff" fillOpacity="0.05" />
        </svg>

        <div className="relative max-w-[1080px] mx-auto px-4 md:px-8 pt-5 md:pt-10 pb-14 md:pb-24">
          <div className="inline-flex items-center gap-2 mb-2 md:mb-3">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-white/90">
              <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span className="text-[10px] md:text-[12px] font-black uppercase tracking-[0.2em] text-white/85">Secure checkout</span>
          </div>

          <h1 className="text-[24px] md:text-[42px] font-black tracking-tight leading-[1.05] mb-3 md:mb-5">
            Review &amp; <span className="text-[#ffd7cf]">pay</span>
          </h1>

          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full pl-3 pr-3.5 py-1.5 text-[11.5px] md:text-[13px] font-bold text-white">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              <span>{tripData.pickup_city}</span>
              {tripData.drop_city && (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="opacity-80"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                  <span>{tripData.drop_city}</span>
                </>
              )}
            </div>

            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-3.5 py-1.5 text-[11.5px] md:text-[13px] font-bold text-white">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              <span>{formatDisplayTime(tripData.trip_time)} · {formatDisplayDate(tripData.trip_date)}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-[1080px] mx-auto px-4 md:px-8 -mt-8 md:-mt-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-8">
          {/* Left Column: cab summary + form */}
          <div className="lg:col-span-7 flex flex-col gap-4 md:gap-6">

            {/* Compact cab summary row */}
            <div className="bg-white rounded-2xl md:rounded-[24px] p-3.5 md:p-6 shadow-[0_4px_24px_rgba(0,0,0,0.04)] border border-gray-50 flex items-center gap-3 md:gap-5">
              <div className="relative w-[84px] md:w-[130px] shrink-0 flex items-center justify-center">
                <div className="absolute w-[70px] h-[70px] md:w-[110px] md:h-[110px] rounded-full bg-gradient-to-tr from-[#f4f7f6] to-[#eef9f9] -z-10 blur-lg"></div>
                <img src={getCarImage(cab?.name)} alt={displayName} className="w-full object-contain drop-shadow-[0_6px_12px_rgba(0,0,0,0.10)]" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 border border-[#21A5A0] text-[#21A5A0] font-black text-[9px] md:text-[10px] uppercase tracking-wider py-0.5 rounded-full whitespace-nowrap">
                    {cabType}
                  </span>
                  <span className="text-[10px] md:text-[11px] font-bold text-[#8a9793] truncate">{MODEL_NAMES[cabTypeId] || "Swift, WagonR"}</span>
                </div>
                <h2 className="text-[18px] md:text-[24px] font-black text-[#1a1a1a] leading-tight truncate">{displayName}</h2>
                <div className="flex items-center gap-2 mt-1 text-[11px] md:text-[12.5px] font-bold text-[#5b6b66]">
                  <span>{cab?.seat_capacity} seats</span>
                  <span className="text-[#cfdad7]">·</span>
                  <span>AC</span>
                  <span className="text-[#cfdad7]">·</span>
                  <span>2 bags</span>
                </div>
              </div>

              <div className="text-right shrink-0 pl-1">
                <div className="text-[10px] font-bold text-[#8a9793] uppercase tracking-wider">Fare</div>
                <div className="text-[18px] md:text-[22px] font-black text-[#1a1a1a] leading-none">₹{priceLabel.toLocaleString()}</div>
                <button
                  onClick={handleExpiryRedirect}
                  className="mt-1.5 inline-flex items-center gap-1 text-[#21A5A0] font-bold text-[11px] border border-[#21A5A0]/30 hover:bg-[#21A5A0]/5 px-2.5 py-1 rounded-full transition-colors"
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                  Switch
                </button>
              </div>
            </div>

            {/* Compact good-to-know: driver note + inclusion chips. Replaces the
                old full "Included in your fare" grid and the "Driver & Cab
                Details" accordion, which pushed the pay action far down. */}
            <div className="bg-white rounded-2xl md:rounded-[24px] p-3.5 md:p-6 shadow-[0_4px_24px_rgba(0,0,0,0.03)] border border-gray-50">
              <div className="flex items-start gap-2.5 mb-3">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#21A5A0" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5">
                  <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
                </svg>
                <p className="text-[12.5px] md:text-[13.5px] font-medium text-[#3d4946] leading-snug">
                  Your driver &amp; cab number arrive on <span className="font-bold text-[#1a1a1a]">WhatsApp + SMS about 2 to 3 hours before pickup</span>. Nothing else to do now.
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5 md:gap-2">
                {INCLUSIONS.map((it) => (
                  <span key={it.label} className="inline-flex items-center gap-1.5 bg-[#f4f7f6] border border-[#e8edec] text-[#3d4946] font-bold text-[11px] md:text-[12px] px-2.5 py-1.5 rounded-full">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="3" className="shrink-0"><polyline points="20 6 9 17 4 12"/></svg>
                    {it.label}
                  </span>
                ))}
              </div>
              <p className="text-[10.5px] md:text-[11.5px] font-medium text-[#9aa6a2] mt-3">
                {isRoundTrip ? (
                  <>Base fare covers up to {Math.ceil(distance || cab?.distance || 0)} km. Driver allowance{driverAllowance > 0 ? ` (₹${driverAllowance.toLocaleString()})` : ""}, toll, parking, state permits and extra km (₹{cab?.fare_per_km || 10}/km) are extra, payable at the trip as per actuals.</>
                ) : (
                  <>Base fare covers up to {Math.ceil(distance || cab?.distance || 0)} km · driver &amp; night allowance included. Tolls, parking, state permits and extra km (₹{cab?.fare_per_km || 10}/km) charged as actuals.</>
                )}
              </p>
            </div>

            {/* Traveller Form */}
            <div id="traveller-form" className="bg-[#f0f6f5] rounded-2xl md:rounded-[24px] p-4 md:p-8 border border-[#e8edec]">
              <BookCabForm />
            </div>
          </div>

          {/* Right Column: Payment */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-6">
              <PaymentCard tripData={tripData} />
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default BookCabMain
